import { IWorkspaceRepository } from './IWorkspaceRepository'
import { WorkspaceSettings, PlatformBranding } from '../models/Workspace'
import { workspaceService } from '../../services/back4app'
import Parse from 'parse'

/** Wraps a promise with a timeout that resolves to `null` (never rejects). */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), ms))
  return Promise.race([promise, timeout])
}

/**
 * Cloud caches the settings row for about a minute per worker. `fresh` asks for an uncached read,
 * and is sent only when asked so every other caller issues the same call as before (an older
 * cloud ignores the param).
 */
function publicSettingsCall(options?: { fresh?: boolean }) {
  return options?.fresh
    ? Parse.Cloud.run('getPublicWorkspaceSettings', { fresh: true })
    : Parse.Cloud.run('getPublicWorkspaceSettings')
}

export class Back4AppWorkspaceRepository implements IWorkspaceRepository {
  async getSettings(options?: { fresh?: boolean }): Promise<WorkspaceSettings | null> {
    try {
      // First try to get settings using the public cloud function.
      // Cap the call at 8 s so a slow connection never stalls the app.
      try {
        const response = await withTimeout(publicSettingsCall(options), 8000)
        if (response && (response as any).success && (response as any).settings) {
          return (response as any).settings
        }
      } catch (publicError: any) {
        // Check if it's a "function not found" error
        if (publicError.message?.includes('Invalid function') || publicError.code === 141) {
          // Import and use static settings service
          const { staticSettingsService } = await import('../../services/staticSettings')
          return await staticSettingsService.loadStaticSettings()
        }
      }

      // A fresh read is for a caller about to save the whole form over the row. The legacy read
      // below ignores `fresh` and can return this tab's old copy or a 60 s old cloud read, so it
      // is never the answer to a failed or timed-out fresh read: report no settings instead.
      if (options?.fresh) return null

      // Fallback to authenticated method
      const settings = await workspaceService.getSettings()
      return settings
    } catch (error: any) {
      // If it's an authentication error, try the public endpoint
      if (error.code === Parse.Error.INVALID_SESSION_TOKEN ||
        error.code === Parse.Error.SESSION_MISSING) {
        try {
          const response = await withTimeout(publicSettingsCall(options), 8000)
          if (response && (response as any).success && (response as any).settings) {
            return (response as any).settings
          }
        } catch (publicError: any) {
          // Check if it's a "function not found" error
          if (publicError.message?.includes('Invalid function') || publicError.code === 141) {
            const { staticSettingsService } = await import('../../services/staticSettings')
            return await staticSettingsService.loadStaticSettings()
          }
        }
      }
      return null
    }
  }

  async updateSettings(settings: Partial<WorkspaceSettings>): Promise<WorkspaceSettings> {
    // No fallback to the legacy positional save: it can't write registration_enabled or the other
    // flags, so it only half-saved and let a failed save look like a success.
    const updatedSettings = await workspaceService.updateSettingsObject(settings)
    if (!updatedSettings) {
      throw new Error('Failed to update workspace settings')
    }
    return updatedSettings
  }

  getPlatformBranding(): PlatformBranding {
    // Default branding - this should be loaded from settings
    return {
      platformName: 'Sevv',
      platformUrl: 'https://app.sevv.com',
      logoUrl: '/logo.png',
      primaryColor: '#389acb'
    }
  }

  async validateDomain(email: string): Promise<boolean> {
    try {
      const settings = await this.getSettings()
      if (!settings?.blocked_domains || settings.blocked_domains.length === 0) {
        return true // No restrictions
      }

      const emailDomain = email.split('@')[1]
      return !settings.blocked_domains.includes(emailDomain) // Return true if domain is NOT blocked
    } catch (error) {
      console.error('Error validating domain:', error)
      return false
    }
  }

  async validateRegistrationEmail(email: string): Promise<{ isValid: boolean; message: string; companyId?: string; companyName?: string; defaultRole?: string }> {
    try {
      const result = await Parse.Cloud.run('validateRegistrationEmail', { email })
      return result
    } catch (error) {
      console.error('Error validating registration email:', error)
      return { isValid: false, message: 'Unable to validate email at this time' }
    }
  }
} 